import { useCallback, useMemo } from 'react'
import { useNewRequestCartStore } from '@/store/newRequestCartStore'
import {
  ServiceSlug,
  DateTimeInstance,
  getSlotType,
  makeBlankSlot,
} from '@/components/pages/pet-sitter/reservations/NewRequest/requestConfig'

// --- Types ---
type FormSlot = ReturnType<typeof makeBlankSlot>
type FormSlotsByService = Record<string, FormSlot[]>

type UpdateSlotsFn = (updater: (prev: FormSlotsByService) => FormSlotsByService) => void

type Params = {
  actualService?: string | null
  formSlotsByService?: FormSlotsByService
  updateFormSlotsByService?: UpdateSlotsFn
}

const pickSharedSchedule = (instance: DateTimeInstance) => ({
  dateStart: instance.dateStart,
  dateEnd: instance.dateEnd,
  timePeriods: instance.timePeriods || [{ timeStart: '', serviceDuration: '' }],
  date: instance.date,
  timeStart: instance.timeStart,
  timeEnd: instance.timeEnd,
  frequency: instance.frequency,
  customDays: instance.customDays,
  serviceDayCount: instance.serviceDayCount || 1,
  serviceDuration: instance.serviceDuration,
})

const mergeSchedule = (existing: DateTimeInstance | null | undefined, instance: DateTimeInstance): DateTimeInstance => {
  const shared = pickSharedSchedule(instance)
  return {
    ...(existing ?? {}),
    ...shared,
  }
}

export function useServiceSlots(paramsOrService?: Params | string | null) {
  const params: Params =
    typeof paramsOrService === 'object' && paramsOrService !== null
      ? paramsOrService
      : { actualService: paramsOrService }

  const storeFormSlotsByService = useNewRequestCartStore((s) => s.formSlotsByService)
  const storeUpdateFormSlotsByService = useNewRequestCartStore((s) => s.updateFormSlotsByService)

  const actualService = params.actualService
  const formSlotsByService = params.formSlotsByService ?? storeFormSlotsByService
  const updateFormSlotsByService = params.updateFormSlotsByService ?? storeUpdateFormSlotsByService

  const currentSlug: ServiceSlug | null = useMemo(() => {
    if (!actualService) return null
    const slug = getSlotType(actualService)
    return slug ? (slug as ServiceSlug) : null
  }, [actualService])

  const addNewSlot = useCallback(() => {
    if (!currentSlug) return
    updateFormSlotsByService((prev) => ({
      ...prev,
      [currentSlug]: [...(prev[currentSlug] ?? []), makeBlankSlot(currentSlug, null)],
    }))
  }, [currentSlug, updateFormSlotsByService])

  const handleSaveSlot = useCallback(
    (slotId: string, instance: DateTimeInstance) => {
      if (!currentSlug) return

      updateFormSlotsByService((prev) => {
        // Ensure all required service keys are present
        const requiredSlugs: ServiceSlug[] = ['promenade', 'garde-chez-vous', 'garde-chez-eux', 'visite']
        const next: Record<ServiceSlug, FormSlot[]> = { ...prev }

        // 1) Update current service slot (full instance)
        if (currentSlug) {
          next[currentSlug] = (prev[currentSlug] ?? []).map((s) => (s.id === slotId ? { ...s, data: instance } : s))
        }

        // 2) Propagate shared schedule to other services
        const shared = pickSharedSchedule(instance)

        requiredSlugs.forEach((slug) => {
          if (slug === currentSlug) return

          const slots = prev[slug] ?? []

          // If slot with same id exists -> update it
          const hasSameId = slots.some((s) => s.id === slotId)

          if (hasSameId) {
            next[slug] = slots.map((s) => (s.id === slotId ? { ...s, data: mergeSchedule(s.data, instance) } : s))
            return
          }

          // Otherwise: update first slot if exists, else create one
          if (slots.length > 0) {
            const [first, ...rest] = slots
            next[slug] = [{ ...first, data: mergeSchedule(first.data, instance) }, ...rest]
          } else {
            const blank = makeBlankSlot(slug, null)
            next[slug] = [{ ...blank, data: { ...shared } }]
          }
        })

        // Ensure all required keys are present
        requiredSlugs.forEach((slug) => {
          if (!next[slug]) next[slug] = []
        })

        return next as any
      })
    },
    [currentSlug, updateFormSlotsByService],
  )

  const removeSlot = useCallback(
    (slotId: string) => {
      if (!currentSlug) return
      updateFormSlotsByService((prev) => {
        const filtered = (prev[currentSlug] ?? []).filter((s) => s.id !== slotId)
        const next = filtered.length ? filtered : [makeBlankSlot(currentSlug, null)]
        return { ...prev, [currentSlug]: next }
      })
    },
    [currentSlug, updateFormSlotsByService],
  )

  return {
    formSlotsByService,
    updateFormSlotsByService,
    currentSlug,
    addNewSlot,
    handleSaveSlot,
    removeSlot,
  }
}
